import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridActionsCellItem,
  GridRowParams,
  GridRenderCellParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import Box from '@mui/material/Box';
import { Info, Edit, Trash2, Merge } from 'lucide-react';
import { LgmDataTableType } from './LgmDataTable';
import {
  CustomAction,
  TruncatedCell,
  useResizeDelay,
  useSortHandler,
  calculateActionsConfig,
  dataGridStyles,
} from './shared/DataGridShared';

export type { CustomAction };

interface LgmDataGridProps<T extends LgmDataTableType> {
  data: T[];
  columns: GridColDef<T>[];
  loading?: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  onInfo?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onMerge?: (row: T) => void;
  actions?: (row: T) => CustomAction<T>[];
  getRowId?: (row: T) => string | number;
  getRowClassName?: (params: GridRowParams<T>) => string;
  /** Row height in pixels. Defaults to 52 (standard). Use 36 for compact density. */
  rowHeight?: number;
}

/**
 * Generic MUI DataGrid wrapper with LGM Navigator features
 * Supports server-side pagination, sorting, and filtering
 * Includes CSV export, column management, and density controls
 */
function LgmDataGrid<T extends LgmDataTableType>({
  data,
  columns,
  loading = false,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel = [],
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onInfo,
  onEdit,
  onDelete,
  onMerge,
  actions: customActionsGetter,
  getRowId,
  getRowClassName,
  rowHeight = 52,
}: LgmDataGridProps<T>) {
  // Use shared hooks for resize delay and sort handling
  const resizeEnabled = useResizeDelay(loading, data.length);
  const handleSortModelChange = useSortHandler(sortModel, onSortModelChange);

  // Calculate actions column configuration. customActionsGetter's array
  // length is not knowable statically (see calculateActionsConfig's
  // docstring) -- and can be row-dependent (e.g. ManufacturerMap.tsx returns
  // a different action set per row), so take the max across the current
  // page of data rather than sampling only the first row, which could
  // under-size the column for a later row with more actions.
  const customActionsCount = customActionsGetter
    ? Math.max(0, ...data.map(row => customActionsGetter(row).length))
    : 0;
  const { hasActions, actionsColumnWidth } = calculateActionsConfig(
    onInfo,
    onEdit,
    onDelete,
    onMerge,
    customActionsGetter,
    customActionsCount
  );

  // Enhance columns with TruncatedCell for those without custom renderCell
  // Also add test identifiers for E2E testing
  const enhancedColumns = columns.map(col => {
    const enhanced: GridColDef<T> = {
      ...col,
      // Add test ID to column header for E2E tests
      headerClassName: `column-${col.field}`,
    };

    if (!col.renderCell && col.type !== 'actions') {
      enhanced.renderCell = (params: GridRenderCellParams) => (
        <TruncatedCell params={params} />
      );
    }

    return enhanced;
  });

  const actionsColumn: GridColDef<T> = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: actionsColumnWidth,
    hideable: false,
    pinnable: false,
    disableReorder: true,
    getActions: (params: GridRowParams<T>) => {
      const actions = [];

      if (onInfo) {
        actions.push(
          <GridActionsCellItem
            key="info"
            icon={<Info size={18} />}
            label="Info"
            title="Info"
            onClick={() => onInfo(params.row)}
          />
        );
      }

      if (onEdit) {
        actions.push(
          <GridActionsCellItem
            key="edit"
            icon={<Edit size={18} />}
            label="Edit"
            title="Edit"
            onClick={() => onEdit(params.row)}
          />
        );
      }

      if (onDelete) {
        actions.push(
          <GridActionsCellItem
            key="delete"
            icon={<Trash2 size={18} />}
            label="Delete"
            title="Delete"
            onClick={() => onDelete(params.row)}
          />
        );
      }

      if (onMerge) {
        actions.push(
          <GridActionsCellItem
            key="merge"
            icon={<Merge size={18} />}
            label="Merge"
            title="Merge"
            onClick={() => onMerge(params.row)}
          />
        );
      }

      // Add custom actions if provided
      if (customActionsGetter) {
        const customActions = customActionsGetter(params.row);
        customActions.forEach((action, index) => {
          const actionLabel = action.title || `Action ${index + 1}`;
          actions.push(
            <GridActionsCellItem
              key={`custom-${index}`}
              icon={action.icon}
              style={action.style}
              label={actionLabel}
              title={actionLabel}
              onClick={() => action.onAction(params.row)}
              showInMenu={action.showInMenu}
            />
          );
        });
      }

      return actions;
    },
  };

  const allColumns = hasActions
    ? [...enhancedColumns, actionsColumn]
    : enhancedColumns;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <DataGridPremium
        rows={data}
        columns={allColumns}
        getRowId={getRowId}
        loading={loading}
        rowCount={rowCount}
        rowHeight={rowHeight}
        columnHeaderHeight={40}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        // With a correct row count of 0 the grid shows its "no rows" overlay, not the "no results"
        // overlay it showed while the footer total was stale; keep the wording callers saw.
        localeText={{ noRowsLabel: 'No results found.' }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        initialState={
          hasActions ? { pinnedColumns: { right: ['actions'] } } : undefined
        }
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
          columnsManagement: {
            getTogglableColumns: columns => {
              // Exclude actions column from the Manage Columns panel
              return columns
                .filter(column => column.field !== 'actions')
                .map(column => column.field);
            },
          },
          columnMenu: {
            // Add test IDs to column menu for E2E tests
            // MUI renders menu as portal in document.body
            slotProps: {
              root: {
                'data-testid': 'column-menu-root',
              },
            },
          },
        }}
        disableRowSelectionOnClick
        disableMultipleRowSelection
        disableColumnSelector={false}
        disableColumnResize={!resizeEnabled}
        autoPageSize={false}
        getRowClassName={getRowClassName}
        sx={dataGridStyles}
      />
    </Box>
  );
}

export default LgmDataGrid;
